import { _ } from "~/i18n";
import { WeatherData, Entry } from "~/weather/interfaces";
import { node } from "~/util/dom";

interface IParseData {
	count: number;
	offsets: Array<number>;
	firstDay: number;
	dayData: Array<Array<Entry>>;
	minTemp: number;
	distTemp: number;
}

interface IOptions {
	width?: number;
	height?: number;
	dayInd?: number;
	move?: boolean;
}

const WIDTH = 350;
const HEIGHT = 250;
const TOP_PADDING = 33; // [%]
const DRAW_AREA = 25; // [%]
const DRAW_BOTTOM = 7; // [%] spodni prekryv
const DAYS_COUNT = 5;
const PER_DAY = 8;
const MIN_TEMP_DISTANCE = 5;
const COLORS = {
	BG: "#fff",
	NIGHT_GRADIENT_START: "rgba(0, 36, 68, 0)",
	NIGHT_GRADIENT_END: "rgba(0, 36, 68, 0.15)",
	GRADIENT_LINE_NORMAL: "rgba(255, 184, 0, 1)",
	GRADIENT_LINE_COLD: "rgba(167, 208, 255, 1)",
	GRADIENT_BG_NORMAL: "rgba(255, 184, 0, 0.8)",
	GRADIENT_BG_COLD: "rgba(167, 208, 255, 0.8)",
	GRADIENT_OVER_START: "rgba(255, 255, 255, 0)",
	GRADIENT_OVER_END: "rgba(255, 255, 255, 1)",
};
const LINE_WIDTH = 3;
const INTERVAL_SECONDS = 24 / PER_DAY * 3600;

export default class WeatherGraph {
	_data: WeatherData;

	_opts: IOptions;

	_dayInd: number;

	_dom: {
		cont: HTMLDivElement;
		canvas: HTMLCanvasElement;
		inner: HTMLDivElement;
		columns: HTMLDivElement;
		timeCircle: HTMLDivElement;
	};

	_ctx: CanvasRenderingContext2D;

	_params: {
		[key: string]: any;
	};

	_columns: Array<{
		column: HTMLDivElement;
		temp: HTMLSpanElement;
		ind: number;
		dayInd: number;
	}>;

	_noPrecips: Array<{
		noPrecip: HTMLSpanElement;
		dayInd: number;
	}>;

	_selected: {
		timeItems: Array<HTMLDivElement>;
		pointItems: Array<HTMLDivElement>;
	};

	_parsedData: IParseData;

	_intID: NodeJS.Timeout;

	constructor(weatherData: WeatherData, opts: IOptions) {
		this._data = weatherData;
		this._opts = {};
		this._dayInd = -1;
		this._dom = {
			cont: node("div", { className: "weather-graph" }),
			canvas: node("canvas"),
			inner: node("div", { className: "inner" }),
			columns: node("div", { className: "columns" }),
			timeCircle: node("div", { className: "time-circle" }),
		};
		this._ctx = this._dom.canvas.getContext("2d");
		this._dom.cont.appendChild(this._dom.inner);
		this._dom.inner.appendChild(this._dom.canvas);
		this._dom.inner.appendChild(this._dom.columns);
		this._dom.columns.appendChild(this._dom.timeCircle);
		this._params = null;
		this._columns = [];
		this._noPrecips = [];
		this._selected = {
			timeItems: [],
			pointItems: [],
		};
		this._parsedData = this._parseData();

		this._buildColumns();
		// co 5 minut update
		this._intID = setInterval(() => {
			this._moveTimeCircle();
		}, 1000 * 5 * 60);

		this.update({
			width: WIDTH,
			height: HEIGHT,
			dayInd: 0,
			...opts,
		});
	}

	destroy() {
		clearInterval(this._intID);
		this._intID = null;
	}

	get container() {
		return this._dom.cont;
	}

	update(opts: IOptions = {}) {
		let dimChange = false;

		if ("width" in opts && typeof opts.width === "number") {
			this._opts.width = opts.width;
			dimChange = true;
		}

		if ("height" in opts && typeof opts.height === "number") {
			this._opts.height = opts.height;
			dimChange = true;
		}

		if (dimChange) {
			// upravime parametry
			this._params = this._getDimension(this._opts.width, this._opts.height);
			// prenastavime
			this._dom.canvas.width = this._params.wholeWidth;
			this._dom.canvas.height = this._params.height;
			this._dom.columns.style.width = `${this._params.wholeWidth}px`;
			this._dom.columns.style.height = `${this._params.height}px`;
			this._dom.cont.style.width = `${this._params.width}px`;
			this._dom.cont.style.height = `${this._params.height}px`;
			// sirky sloupcu
			this._columns.forEach(item => {
				item.column.style.width = `${this._params.intervalWidth}px`;
			});
			// bez srazek - umisteni
			this._noPrecips.forEach(item => {
				item.noPrecip.style.width = `${this._params.width}px`;
				item.noPrecip.style.left = `${this._parsedData.offsets[item.dayInd] * this._params.intervalWidth}px`;
			});
			// prekreslime graf
			this._draw();
			// nastavime tupku
			this._moveTimeCircle();
		}

		if ("dayInd" in opts && typeof opts.dayInd === "number" && opts.dayInd !== this._dayInd) {
			this._dayInd = opts.dayInd;
			this._noPrecips.forEach(item => {
				item.noPrecip.classList[parseInt(item.noPrecip.getAttribute("data-dayInd")) === this._dayInd ? "remove" : "add"]("hide");
			});
			this._resetSelected();
			// pro animaci - zadne srazky
			this._dom.inner.classList.add("anim");
			this._dom.inner.style.left = `${-this._parsedData.offsets[this._dayInd] * this._params.intervalWidth}px`;
			// animace
			const to = 1000 + 1;

			setTimeout(() => {
				// vybereme sloupce podle intervalu
				this._selectColumns();
			}, to / 2);

			setTimeout(() => {
				this._dom.inner.classList.remove("anim");
				this._moveTimeCircle();
			}, to);
		}
		if ("dayInd" in opts && typeof opts.dayInd === "number" && "move" in opts && opts.move) {
			this._dom.inner.style.left = `${-this._parsedData.offsets[this._dayInd] * this._params.intervalWidth}px`;
		}
	}

	/**
	 * Sirka a vyska canva
	 *
	 * @param  width   sirka canvasu
	 * @param  height  vyska canvasu
	 */
	_getDimension(width: number, height: number) {
		// odkud shora zacit kreslit?
		const topY = Math.round(height * TOP_PADDING / 100);
		// celkova plocha ke kresleni krivek
		const drawDist = Math.round(height * DRAW_AREA / 100);
		const bottomY = topY + drawDist + Math.round(height * DRAW_BOTTOM / 100);
		// sirka jednoho intervalu (3hod)
		const intervalWidth = width / PER_DAY;
		// pulka sirky jednoho intervalu
		const intervalWidthMiddle = intervalWidth / 2;

		return {
			width,
			wholeWidth: intervalWidth * this._parsedData.count,
			height,
			topY,
			drawMaxY: topY + drawDist,
			drawDist,
			bottomY,
			intervalWidth,
			intervalWidthMiddle,
		};
	}

	_buildColumns() {
		const frag = document.createDocumentFragment();
		let ind = 0;

		this._parsedData.dayData.forEach((dayItems, dayInd) => {
			let wasPrecip = false;

			dayItems.forEach(day => {
				const column = node("div", { className: "column" });

				column.appendChild(node("span", { className: "time", textContent: day.my.hourTime }));
				column.appendChild(day.my.svg());
				const temp = node("span", { className: "temp", textContent: day.my.temp });

				column.appendChild(temp);
				// vitr
				const wind = node("span", { className: "wind", title: day.my.wind.tooltip });

				wind.appendChild(day.my.wind.svg());
				wind.appendChild(node("span", { className: "velocity", textContent: day.my.wind.value.toString() }));
				wind.appendChild(node("span", { className: "unit", textContent: "m/s" }));
				column.appendChild(wind);
				// srazky - dest/snih
				if (day.my.precip) {
					wasPrecip = true;
					const precip = node("span", { className: "precip", title: day.my.precip.tooltip });

					if (day.my.precip.isSnow) {
						precip.classList.add("snow");
					}

					const precipValue = node("span", { className: "value", textContent: day.my.precip.value });
					const precipVisual = node("span", { className: "visual" });

					precipVisual.style.height = `${day.my.precip.height}px`;
					precip.appendChild(precipValue);
					precip.appendChild(precipVisual);
					column.appendChild(precip);
				}

				frag.appendChild(column);

				this._columns.push({
					column,
					temp,
					ind,
					dayInd,
				});
				ind++;
			});

			if (dayInd === 0 && !wasPrecip) {
				// vyjimka pro prvni den
				const rest = PER_DAY - dayItems.length;
				// polozky z dalsiho dne
				const items = this._parsedData.dayData[1].slice(0, rest);

				wasPrecip = !!items.filter(item => item.my.precip).length;
			}

			if (!wasPrecip) {
				const noPrecip = node("span", { className: "no-precip", textContent: _("weather.graph.noPrecip") });

				noPrecip.setAttribute("data-dayInd", dayInd.toString());
				frag.appendChild(noPrecip);

				this._noPrecips.push({
					noPrecip,
					dayInd,
				});
			}
		});

		this._dom.columns.appendChild(frag);
	}

	_parseData() {
		const firstDay = this._data.entries[0].dayId;
		const dayData: Array<Array<Entry>> = [];
		let count = 0;
		const offsets: Array<number> = [];
		let minTemp = Infinity;
		let maxTemp = -Infinity;

		const tempMinMax = entry => {
			minTemp = Math.min(minTemp, entry.temp);
			maxTemp = Math.max(maxTemp, entry.temp);
		};

		for (let index = 0; index < DAYS_COUNT; index++) {
			const dayItems = this._data.entries.filter(entry => entry.dayId === firstDay + index);

			dayItems.forEach(entry => tempMinMax(entry));
			offsets.push(count);
			count += dayItems.length;
			dayData.push(dayItems);
		}

		return {
			count,
			offsets,
			firstDay,
			dayData,
			minTemp,
			// zvysime max teplotu kvuli peakum
			distTemp: Math.max(maxTemp - minTemp, MIN_TEMP_DISTANCE),
		};
	}

	_draw() {
		if (!this._ctx) {
			return;
		}

		// canvas a pozadi
		this._ctx.clearRect(0, 0, this._params.width, this._params.height);
		this._ctx.fillStyle = COLORS.BG;
		this._ctx.fillRect(0, 0, this._params.width, this._params.height);

		// dny a noci
		const topDrawDist = this._params.topY + this._params.drawDist;
		const nightGradient = this._ctx.createLinearGradient(0, 0, 0, topDrawDist);

		nightGradient.addColorStop(0.2, COLORS.NIGHT_GRADIENT_START);
		nightGradient.addColorStop(1, COLORS.NIGHT_GRADIENT_END);
		const lineGradient = this._ctx.createLinearGradient(0, 0, this._params.wholeWidth, 0);
		const bgGradient = this._ctx.createLinearGradient(0, 0, this._params.wholeWidth, 0);
		const overGradient = this._ctx.createLinearGradient(0, 0, 0, this._params.bottomY);

		overGradient.addColorStop(0.5, COLORS.GRADIENT_OVER_START);
		overGradient.addColorStop(1, COLORS.GRADIENT_OVER_END);

		this._ctx.fillStyle = nightGradient;

		let ptr = 0;
		const pixels = [];
		const lastInd = (PER_DAY * DAYS_COUNT) - 1;

		this._parsedData.dayData.forEach(dayItems => {
			dayItems.forEach(day => {
				if (!day.isDay || day.my.sunriseRatio !== null || day.my.sunsetRatio !== null) {
					let x1 = ptr * this._params.intervalWidth;
					let x2 = this._params.intervalWidth;

					// svitani/zapad slunka
					if (day.my.sunriseRatio !== null) {
						x2 *= day.my.sunriseRatio;
					} else if (day.my.sunsetRatio !== null) {
						x1 += day.my.sunsetRatio * this._params.intervalWidth;
						x2 = (1 - day.my.sunsetRatio) * this._params.intervalWidth;
					}

					this._ctx.fillRect(x1, 0, x2, topDrawDist);
				}

				const pixelX = -this._params.intervalWidthMiddle + (this._params.intervalWidth * (ptr + 1));
				const tr = 1 - ((day.temp - this._parsedData.minTemp) / this._parsedData.distTemp);
				const pixelY = Math.round(this._params.drawDist * tr) + this._params.topY;
				const color = day.temp <= 0 ? COLORS.GRADIENT_LINE_COLD : COLORS.GRADIENT_LINE_NORMAL;
				const bgColor = day.temp <= 0 ? COLORS.GRADIENT_BG_COLD : COLORS.GRADIENT_BG_NORMAL;

				if (ptr === 0) {
					pixels.push({
						x: 0,
						y: pixelY,
					});
					lineGradient.addColorStop(0, color);
					bgGradient.addColorStop(0, bgColor);
				}

				pixels.push({
					x: pixelX,
					y: pixelY,
				});
				lineGradient.addColorStop(pixelX / this._params.wholeWidth, color);
				bgGradient.addColorStop(pixelX / this._params.wholeWidth, bgColor);
				this._columns[ptr].temp.style.top = `${pixelY}px`;

				if (ptr === lastInd) {
					pixels.push({
						x: this._params.wholeWidth - 1,
						y: pixelY,
					});
					lineGradient.addColorStop(1, color);
					bgGradient.addColorStop(1, color);
				}

				ptr++;
			});
		});

		// vypln
		this._drawCurve(pixels, true);
		// nejdrive bilou
		this._ctx.fillStyle = COLORS.BG;
		this._ctx.fill();
		// pak gradient s barvou
		this._ctx.fillStyle = bgGradient;
		this._ctx.fill();
		// pak gradient s prechodem dolu
		this._ctx.fillStyle = overGradient;
		this._ctx.fill();
		this._ctx.closePath();

		// krivka - tlusta cara
		this._drawCurve(pixels);

		// cara
		this._ctx.strokeStyle = lineGradient;
		this._ctx.lineWidth = LINE_WIDTH;
		this._ctx.stroke();
		this._ctx.closePath();
	}

	_drawCurve(pixels: Array<{
		x: number;
		y: number;
	}>, filledPath?) {
		if (pixels.length < 2) {
			return;
		}

		this._ctx.beginPath();
		this._ctx.lineTo(pixels[0].x, pixels[0].y);

		const lastPixel = pixels[pixels.length - 1];

		for (let index = 0; index < pixels.length - 1; index++) {
			const p0 = index > 0 ? pixels[index - 1] : pixels[0];
			const p1 = pixels[index];
			const p2 = pixels[index + 1];
			const p3 = index === pixels.length - 2 ? p2 : pixels[index + 2];

			const cp1x = p1.x + ((p2.x - p0.x) / 6);
			const cp1y = p1.y + ((p2.y - p0.y) / 6);

			const cp2x = p2.x - ((p3.x - p1.x) / 6);
			const cp2y = p2.y - ((p3.y - p1.y) / 6);

			this._ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
		}

		// posledni bod krivky
		this._ctx.lineTo(this._params.wholeWidth - 1, lastPixel.y);

		// uzavreni krivky pro vypln
		if (filledPath) {
			this._ctx.lineTo(this._params.wholeWidth - 1, this._params.bottomY);
			this._ctx.lineTo(0, this._params.bottomY);
			//this._ctx.lineTo(pixels.x, pixels.y);
		}
	}

	_resetSelected() {
		while (this._selected.timeItems.length) {
			this._selected.timeItems.pop().classList.remove("hide-time");
		}

		while (this._selected.pointItems.length) {
			this._selected.pointItems.pop().classList.remove("hide-point");
		}
	}

	_selectColumns() {
		// zjistime elementy
		const firstLeft = this._columns.filter(item => item.dayInd === this._dayInd)[0];

		// lava strana co je s vama?
		if (firstLeft.ind > 0) {
			this._selected.pointItems.push(this._columns[firstLeft.ind - 1].column);
		}

		this._selected.timeItems.push(firstLeft.column);
		// prava strana pocujete ma?
		if (firstLeft.ind + PER_DAY < this._columns.length) {
			this._selected.timeItems.push(this._columns[firstLeft.ind + PER_DAY].column);
		}
		this._selected.pointItems.push(this._columns[firstLeft.ind + PER_DAY - 1].column);

		// nastavime
		this._selected.timeItems.forEach(el => el.classList.add("hide-time"));
		this._selected.pointItems.forEach(el => el.classList.add("hide-point"));
	}

	_moveTimeCircle() {
		if (!this._params) {
			return;
		}

		const startHour = this._data.entries[0].my.hour;
		const dateObj = this._data.place.my.dateObj;
		const ts = Date.now() - this._data.place.my.ts; // ms
		const seconds = ((dateObj.getHours() - startHour) * 3600) + (dateObj.getMinutes() * 60) + Math.floor(ts / 1000);
		const ind = Math.floor(seconds / INTERVAL_SECONDS);
		const ratio = (seconds % INTERVAL_SECONDS) / INTERVAL_SECONDS;
		const posX = (ind * this._params.intervalWidth) + Math.floor(ratio * this._params.intervalWidth);

		// omezeni
		//posX = Math.max(Math.min(posX, this._params.wholeWidth - 1), 0);

		// nastavime
		this._dom.timeCircle.style.left = `${posX}px`;
	}
}
